export interface DocumentStatus {
  isPaid: boolean;
  isPending: boolean;
  isRejected: boolean;
  isCancelled: boolean;
  isCompleted: boolean;
  isRefunded: boolean;
  isRefundPending: boolean;
  documentType: string;
  emailSubjectPrefix: string;
  amountLabel: string;
  emailAttachmentLabel: string;
  badgeText: string;
  statusText: string;
  badgeColorClass: string;
  badgeTextClass: string;
  badgeBorderClass: string;
  hexColor: string;
  rgbColor: [number, number, number];
}

export function resolveStatus(statusStr: string, paymentStatusStr?: string, enableTax: boolean = false): DocumentStatus {
  const s = String(statusStr || '').toUpperCase();
  const ps = String(paymentStatusStr || '').toUpperCase();
  
  const combined = `${s} ${ps}`;
  const isPaid = (combined.includes('PAID') && !combined.includes('UNPAID')) || combined.includes('APPROV') || combined.includes('COMPLET') || combined.includes('SUCCESS') || combined.includes('VERIFIED');
  const isCancelled = combined.includes('CANCEL');
  const isRejected = combined.includes('REJECT') || combined.includes('FAIL');
  
  const exactS = s.replace(/\s+/g, '_');
  const exactPS = ps.replace(/\s+/g, '_');
  const pendingRefunds = ['REFUND_PENDING', 'REFUND_REQUESTED', 'REFUND_PROCESSING', 'REFUND_INITIATED', 'REFUND_STARTED', 'REFUND_REQUEST', 'REFUND_PROCESS', 'REFUND_START'];
  const completedRefunds = ['REFUNDED', 'REFUND_COMPLETED', 'REFUND_SUCCESS'];
  
  const isRefundPending = pendingRefunds.includes(exactS) || pendingRefunds.includes(exactPS);
  const isRefunded = (completedRefunds.includes(exactS) || completedRefunds.includes(exactPS)) && !isRefundPending;
  
  const isPending = !isPaid && !isCancelled && !isRejected && !isRefunded;

  let documentType = 'Invoice';
  let emailSubjectPrefix = 'Invoice / Receipt';
  let amountLabel = 'Total Amount';
  let emailAttachmentLabel = 'Invoice attached.';
  
  let badgeText = 'UNKNOWN STATUS';
  let statusText = 'Unknown Status';
  let badgeColorClass = 'bg-gray-500/10';
  let badgeTextClass = 'text-gray-400';
  let badgeBorderClass = 'border-gray-500/30';
  let hexColor = '#9ca3af'; // gray-400
  let rgbColor: [number, number, number] = [156, 163, 175]; // gray-400

  if (isRefunded) {
    documentType = 'Refund Confirmation / Refund Record';
    emailSubjectPrefix = 'Refund Confirmation';
    amountLabel = 'Total Refunded';
    emailAttachmentLabel = 'Refund confirmation record attached.';
    badgeText = '✓ REFUNDED';
    statusText = 'Refunded';
    badgeColorClass = 'bg-purple-500/10';
    badgeTextClass = 'text-purple-400';
    badgeBorderClass = 'border-purple-500/30';
    hexColor = '#a855f7'; // purple-500
    rgbColor = [168, 85, 247];
  } else if (isRefundPending) {
    documentType = 'Cancellation / Refund Pending Record';
    emailSubjectPrefix = 'Cancellation / Refund Pending';
    amountLabel = 'Total Paid';
    emailAttachmentLabel = 'Cancellation and refund pending record attached.';
    badgeText = '⏳ REFUND PENDING';
    statusText = 'Refund Pending';
    badgeColorClass = 'bg-amber-500/10';
    badgeTextClass = 'text-amber-400';
    badgeBorderClass = 'border-amber-500/30';
    hexColor = '#f59e0b'; // amber-500
    rgbColor = [245, 158, 11];
  } else if (isCancelled && isPaid) {
    documentType = 'Payment Received / Cancellation Record';
    emailSubjectPrefix = 'Cancelled Invoice';
    amountLabel = 'Total Paid';
    emailAttachmentLabel = 'Payment and cancellation record attached.';
    badgeText = '✕ CANCELLED (PAID)';
    statusText = 'Cancelled (Paid)';
    badgeColorClass = 'bg-red-500/10';
    badgeTextClass = 'text-red-400';
    badgeBorderClass = 'border-red-500/30';
    hexColor = '#ef4444'; // red-500
    rgbColor = [239, 68, 68];
  } else if (isCancelled) {
    documentType = 'Cancelled Order Record';
    emailSubjectPrefix = 'Cancelled Invoice';
    amountLabel = 'Total Amount';
    emailAttachmentLabel = 'Cancelled order record attached.';
    badgeText = '✕ CANCELLED';
    statusText = 'Cancelled';
    badgeColorClass = 'bg-red-500/10';
    badgeTextClass = 'text-red-400';
    badgeBorderClass = 'border-red-500/30';
    hexColor = '#ef4444'; // red-500
    rgbColor = [239, 68, 68];
  } else if (isRejected) {
    documentType = 'Payment Record / Order Record';
    emailSubjectPrefix = 'Rejected Invoice';
    amountLabel = 'Total Amount';
    emailAttachmentLabel = 'Payment / order record attached.';
    badgeText = '✕ REJECTED';
    statusText = 'Payment Rejected';
    badgeColorClass = 'bg-red-500/10';
    badgeTextClass = 'text-red-400';
    badgeBorderClass = 'border-red-500/30';
    hexColor = '#ef4444'; // red-500
    rgbColor = [239, 68, 68];
  } else if (isPending) {
    documentType = 'Invoice / Payment Request';
    emailSubjectPrefix = 'Pending Invoice / Receipt';
    amountLabel = 'Total Due';
    emailAttachmentLabel = 'Invoice / payment request attached.';
    badgeText = '⏳ PENDING PAYMENT';
    statusText = 'Pending Payment';
    badgeColorClass = 'bg-amber-500/10';
    badgeTextClass = 'text-amber-400';
    badgeBorderClass = 'border-amber-500/30';
    hexColor = '#f59e0b'; // amber-500
    rgbColor = [245, 158, 11];
  } else if (isPaid) {
    documentType = 'Paid Invoice / Receipt';
    emailSubjectPrefix = 'Paid Invoice / Receipt';
    amountLabel = 'Total Paid';
    emailAttachmentLabel = 'Payment receipt / invoice attached.';
    badgeText = '✓ PAID RECEIPT';
    statusText = 'Paid & Verified';
    badgeColorClass = 'bg-emerald-500/10';
    badgeTextClass = 'text-emerald-400';
    badgeBorderClass = 'border-emerald-500/30';
    hexColor = '#10b981'; // emerald-500
    rgbColor = [16, 185, 129];
  }

  return {
    isPaid,
    isPending,
    isRejected,
    isCancelled,
    isCompleted: isPaid && !isCancelled && !isRefunded,
    isRefunded,
    isRefundPending,
    documentType,
    emailSubjectPrefix,
    amountLabel,
    emailAttachmentLabel,
    badgeText,
    statusText,
    badgeColorClass,
    badgeTextClass,
    badgeBorderClass,
    hexColor,
    rgbColor
  };
}
